import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { RenovationsSystems } from '../../types';
import {
  isLogisticsRowComplete,
  LOGISTICS_SERVICES,
  LogisticsRow,
  parseUtilitiesTable,
  RequiredMark,
} from '../../lib/requiredFields';

const SYSTEM_AGE_FIELDS: { key: keyof RenovationsSystems; label: string; placeholder: string; numberOnly?: boolean }[] = [
  { key: 'furnace_age', label: 'Furnace Age (years) or Year Replaced', placeholder: 'e.g., 5 or 2018' },
  { key: 'air_conditioner_age', label: 'Air Conditioner Age (years) or Year Replaced', placeholder: 'e.g., 3 or 2020' },
  { key: 'water_heater_age', label: 'Water Heater Age (years) or Year Replaced', placeholder: 'e.g., 8 or 2015' },
  { key: 'windows_age', label: 'Windows Age (years) or Year Replaced', placeholder: 'e.g., 10 or 2013' },
  { key: 'roof_age', label: 'Roof Age (years) or Year Replaced', placeholder: 'e.g., 7 or 2016' },
  { key: 'hvac_zones', label: 'HVAC Zones', placeholder: 'Number of HVAC zones', numberOnly: true },
];

function isNA(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toUpperCase() === 'N/A';
}

export default function Section9Renovations() {
  const { state, saveSection } = useFormContext();
  const [data, setData] = useState<RenovationsSystems>({
    intake_id: '',
  });

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (data.intake_id) return;
    if (state.formData.renovations_systems) {
      const loaded = { ...state.formData.renovations_systems } as any;
      // Rehydrate utilities_table from persisted JSON so N/A / row state survive reload
      if (!loaded.utilities_table) {
        loaded.utilities_table = parseUtilitiesTable(loaded);
      }
      setData(loaded);
    } else if (state.intake) {
      setData(prev => ({ ...prev, intake_id: state.intake!.id }));
    }
  }, [state.formData.renovations_systems, state.intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.intake && data.intake_id) {
      saveSection('renovations_systems', data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.intake]);

  const updateField = (field: keyof RenovationsSystems | string, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const isReq = (key: string) =>
    state.requiredFields.some(f => f.section === 'renovations_systems' && f.field_key === key);

  const logisticsRequired = isReq('utilities_logistics');

  const commitUtilitiesTable = (updated: Record<string, LogisticsRow>) => {
    updateField('utilities_table' as any, updated);
    updateField('utilities_vendors', JSON.stringify(updated));
    updateField('average_costs', JSON.stringify(updated));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Renovations & Systems</h2>

        <div className="space-y-6">
          {/* Renovations and Updates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Renovations and Updates
            </label>
            <p className="text-sm text-brand-coral mb-3">
              For each renovation, please include the year it was done — or an estimate (e.g., &quot;~ 5 years ago&quot;).
              A timeframe is required for every renovation you list.
            </p>

            {(() => {
              const rows: { description: string; year: string }[] =
                (data as any).renovations_list || [{ description: '', year: '' }];

              const commit = (next: { description: string; year: string }[]) => {
                updateField('renovations_list' as any, next);
                const summary = next
                  .filter((r) => r.description.trim() !== '')
                  .map((r) => `${r.description.trim()} — ${r.year.trim() || 'year not provided'}`)
                  .join('\n');
                updateField('renovations_updates', summary);
              };

              return (
                <div className="space-y-2">
                  {rows.map((row, idx) => {
                    const missingYear = row.description.trim() !== '' && row.year.trim() === '';
                    return (
                      <div key={idx} className="flex gap-2 items-start">
                        <input
                          type="text"
                          value={row.description}
                          onChange={(e) => {
                            const next = [...rows];
                            next[idx] = { ...row, description: e.target.value };
                            commit(next);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm"
                          placeholder="e.g., Kitchen remodel, new roof, finished basement"
                        />
                        <div className="w-40">
                          <input
                            type="text"
                            value={row.year}
                            onChange={(e) => {
                              const next = [...rows];
                              next[idx] = { ...row, year: e.target.value };
                              commit(next);
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm ${missingYear ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                            placeholder='Year or "~ 5 yrs ago"'
                          />
                          {missingYear && <p className="text-red-500 text-xs mt-0.5">Year required</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const next = rows.filter((_, i) => i !== idx);
                            commit(next.length ? next : [{ description: '', year: '' }]);
                          }}
                          className="px-2 py-2 text-gray-400 hover:text-brand-coral"
                          aria-label="Remove renovation"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => commit([...rows, { description: '', year: '' }])}
                    className="text-sm text-brand-primary font-medium hover:underline"
                  >
                    + Add another renovation
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Major Insurance Claims */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Have you had any major insurance claims?
            </label>
            <p className="text-sm text-brand-coral mb-3">
              *** Please note, any report of damage to your insurance company is considered a claim,
              even if you did not receive any money from your insurance company.
            </p>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="major_insurance_claims"
                  checked={data.major_insurance_claims === true}
                  onChange={() => updateField('major_insurance_claims', true)}
                  className="mr-2"
                />
                Yes
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="major_insurance_claims"
                  checked={data.major_insurance_claims === false}
                  onChange={() => updateField('major_insurance_claims', false)}
                  className="mr-2"
                />
                No
              </label>
            </div>

            {data.major_insurance_claims === true && (
              <textarea
                value={(data as any).insurance_claims_details || ''}
                onChange={(e) => updateField('insurance_claims_details' as any, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                rows={3}
                placeholder="Please provide details about the claims (type, date, resolution)"
              />
            )}
          </div>

          {/* System Ages */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              System Ages
              {SYSTEM_AGE_FIELDS.some(f => isReq(f.key as string)) && <RequiredMark />}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Provide the approximate ages or replacement years for major systems, or mark N/A.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SYSTEM_AGE_FIELDS.map(({ key, label, placeholder, numberOnly }) => {
                const value = (data as any)[key];
                const na = isNA(value);
                return (
                  <div key={key as string}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {label}{isReq(key as string) && <RequiredMark />}
                    </label>
                    <input
                      type={numberOnly && !na ? 'number' : 'text'}
                      value={na ? '' : (value ?? '')}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (numberOnly) {
                          updateField(key, raw === '' ? undefined : parseInt(raw, 10) || raw);
                        } else {
                          updateField(key, raw);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:bg-gray-100"
                      placeholder={placeholder}
                      min={numberOnly ? 1 : undefined}
                      max={numberOnly ? 10 : undefined}
                      disabled={na}
                    />
                    <label className="flex items-center mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={na}
                        onChange={(e) => updateField(key, e.target.checked ? 'N/A' : undefined)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">N/A</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* System & House Logistics Table */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              System &amp; House Logistics{logisticsRequired && <RequiredMark />}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Enter the company, cost, and frequency for each service — or mark the row N/A.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left px-3 py-2 font-medium text-gray-700 border border-gray-200 w-1/4">Service</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 border border-gray-200">Company</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 border border-gray-200 w-28">Cost</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 border border-gray-200 w-36">Frequency</th>
                    <th className="text-center px-2 py-2 font-medium text-gray-700 border border-gray-200 w-16">N/A</th>
                  </tr>
                </thead>
                <tbody>
                  {LOGISTICS_SERVICES.map((service) => {
                    const tableData: Record<string, LogisticsRow> =
                      (data as any).utilities_table || {};
                    const row = tableData[service] || { company: '', cost: '', frequency: '', na: false };
                    const complete = isLogisticsRowComplete(row);
                    const showError = logisticsRequired && !complete;
                    const hasCompany = row.company.trim() !== '';
                    const missingCost = !row.na && hasCompany && row.cost.trim() === '';
                    const missingFrequency = !row.na && hasCompany && row.frequency === '';

                    return (
                      <tr key={service} className={showError ? 'bg-red-50' : ''}>
                        <td className="px-3 py-1 border border-gray-200 font-medium text-gray-700">{service}</td>
                        <td className="px-1 py-1 border border-gray-200">
                          <input
                            type="text"
                            value={row.na ? '' : row.company}
                            disabled={!!row.na}
                            onChange={(e) => {
                              const updated = {
                                ...tableData,
                                [service]: { ...row, company: e.target.value, na: false },
                              };
                              commitUtilitiesTable(updated);
                            }}
                            className="w-full px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-primary rounded text-sm disabled:bg-gray-100"
                            placeholder="Company name"
                          />
                        </td>
                        <td className="px-1 py-1 border border-gray-200">
                          <input
                            type="text"
                            value={row.na ? '' : row.cost}
                            disabled={!!row.na}
                            onChange={(e) => {
                              const updated = {
                                ...tableData,
                                [service]: { ...row, cost: e.target.value, na: false },
                              };
                              commitUtilitiesTable(updated);
                            }}
                            className={`w-full px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-primary rounded text-sm disabled:bg-gray-100 ${missingCost ? 'border border-red-400 bg-red-50' : ''}`}
                            placeholder="e.g., $150"
                          />
                          {missingCost && <p className="text-red-500 text-xs mt-0.5 px-1">Required</p>}
                        </td>
                        <td className="px-1 py-1 border border-gray-200">
                          <select
                            value={row.na ? '' : row.frequency}
                            disabled={!!row.na}
                            onChange={(e) => {
                              const updated = {
                                ...tableData,
                                [service]: { ...row, frequency: e.target.value, na: false },
                              };
                              commitUtilitiesTable(updated);
                            }}
                            className={`w-full px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-primary rounded text-sm disabled:bg-gray-100 ${missingFrequency ? 'border border-red-400 bg-red-50' : ''}`}
                          >
                            <option value="">Select...</option>
                            <option value="per_visit">Per Visit</option>
                            <option value="monthly">Per Month</option>
                            <option value="quarterly">Per Quarter</option>
                            <option value="biannually">Bi-Annually</option>
                            <option value="annually">Annually</option>
                          </select>
                          {missingFrequency && <p className="text-red-500 text-xs mt-0.5 px-1">Required</p>}
                        </td>
                        <td className="px-2 py-1 border border-gray-200 text-center">
                          <input
                            type="checkbox"
                            checked={!!row.na}
                            onChange={(e) => {
                              const updated = {
                                ...tableData,
                                [service]: e.target.checked
                                  ? { company: '', cost: '', frequency: '', na: true }
                                  : { company: '', cost: '', frequency: '', na: false },
                              };
                              commitUtilitiesTable(updated);
                            }}
                            aria-label={`${service} N/A`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {logisticsRequired && (
              <p className="text-xs text-gray-500 mt-2">
                Every row must have company + cost + frequency, or be marked N/A, before submitting.
              </p>
            )}
          </div>

          {/* Systems Fueled By */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Systems Fueled By</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Heating', field: 'fuel_heating' },
                { label: 'Hot Water Heater', field: 'fuel_hot_water_heater' },
                { label: 'Fireplace', field: 'fuel_fireplace' },
              ].map(({ label, field }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type="text"
                    value={(data as any)[field] || ''}
                    onChange={(e) => updateField(field as any, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="e.g., Natural gas, oil, electric, propane"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Warranties */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Active Warranties or Service Contracts
            </label>
            <textarea
              value={(data as any).warranties || ''}
              onChange={(e) => updateField('warranties' as any, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={3}
              placeholder="e.g., Home warranty through XYZ Company, appliance warranties, system service contracts, etc."
            />
          </div>
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            This information helps buyers understand the condition and maintenance history of major systems.
            Don&apos;t worry if you don&apos;t have exact dates — estimates or N/A are fine. This is the final section!
          </p>
        </div>
      </div>
    </div>
  );
}
